/**
 * Tabs component module.
 * @module shared/components/Tabs
 *
 */
import React, {useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import Dropdown from './Dropdown';
import {fill, fillMaxWidth, layoutAttribute} from './cssUtils';
import Icon from './Icon';

const _Wrapper = styled.div`
  //display: inline-block;
  ${fill()};
  min-width: ${props => props.minWidth};
  ${fillMaxWidth};
  margin: 5px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  display: flex;
  flex-direction: column;
  ${layoutAttribute.css}
`;

const _Content = styled.div`
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const _TabSet = styled.ul`
  display: flex;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  background-color: ${props => {
    if (props.theme.neumorphic) return props.theme.tileBackgroundColor;
    return props.theme.dark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
  }};
  line-height: 30px;
  max-height: 30px;
  padding: 1px;
  margin: 0 auto 10px auto;
  border-radius: 15px;
  width: ${props => props.width};
  min-width: ${props => props.width};
  max-width: 90%;
  list-style-type: none;
  overflow: hidden;
  white-space: nowrap;
  z-index: 0;
  ${props => props.theme.neumorphic && 'box-shadow: inset 1px 1px 1px 0 rgba(0, 0, 0, 0.3), inset -1px -1px 1px 0 rgba(255, 255, 255, 0.15);'}

  & li {
    box-sizing: border-box;
  }
`;

const _Tab = styled.li`
  width: ${props => props.tabWidthNum + 'px'};
  cursor: pointer;
  height: 28px;
  max-height: 28px;
  display: inline-block;
  z-index: 0;

  & img {
    position: relative;
    vertical-align: middle;
    top: -1px;
    width: 20px;
    height: 20px;
    margin-right: ${props => (props.addMargin ? '5px' : 0)};
  }
`;

const _Title = styled.div`
  text-align: center;
  color: ${props => props.theme.dark ? 'rgba(255,255,255, 0.9)' : 'rgba(0,0,0, 0.9)'};
  font-family: ${props => props.theme.primaryButtonText.name};
  font-size: 12px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  z-index: 0;
  padding: 5px;
`;

const _Slider = styled.div`
  position: absolute;
  overflow: auto;
  left: ${props => props.offset + 'px'};
  height: 28px;
  width: ${props => props.width};
  background-color: ${props => {
    if (props.theme.neumorphic) return props.theme.tileBackgroundColor;
    return props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  }};
  box-shadow: ${(props) => props.theme.neumorphic ? `0 0 0 ${props.tabsWidth} rgb(255 255 255 / 10%)` : ''};
  border-radius: 15px;
  transition: left 0.5s;
  z-index: -1;

  & div {
    position: absolute;
    width: 100%;
    height: 100%;
    ${props => props.theme.neumorphic && `
      background-color: rgba(255, 255, 255, 0.06);
      border-radius: 15px;
      box-shadow: inset 1px 1px rgb(255 255 255 / 15%), inset -1px -1px rgb(0 0 0 / 30%), 1px 1px 4px 0px rgb(0 0 0 / 30%);`}
  }

  & span {
    display: block;
    position: relative;
    height: 2px;
    background-color: ${props => props.theme.primaryColor};
    bottom: -26px;
  }
`;

const _Dropdown = styled.div`
  ${fillMaxWidth()}
  margin: auto;
  font-family: ${props => props.theme.primaryButtonText};

  & .DropdownWrapper {
    //width: fit-content;
    margin: auto;
  }
`;

/**
 * Tabs component
 * @name Tabs
 */
const Tabs = props => {
  const {tabs, displayTabTitles, minTabWidth, tabWidth, defaultSelection, onChange, externalTabChanger} = props;
  const tabKeyMap = {};
  const componentRef = useRef();
  const tabSetRef = useRef();
  let sliderOffset = null;

  tabs.forEach((tab, idx) => {
    tabKeyMap[tab.title] = tab;
    tabKeyMap[tab.title].position = idx;
  });

  const [tabKey, setTabKey] = useState(tabs[defaultSelection]?.title);
  const [adjustedTabWidthNum, setAdjustedTabWidthNum] = useState(Number(tabWidth.replace('px', '')));
  const [displayTabsDropdown, setDisplayTabsDropdown] = useState(adjustedTabWidthNum < Number(minTabWidth.replace('px', '')));
  const [listenerExists, setListenerExists] = useState(false);

  const recalculateTabWidth = () => {
    const componentOffsetWidth = componentRef.current && componentRef.current.offsetWidth;
    const tabSetOffsetWidth = adjustedTabWidthNum * tabs.length;

    if (tabSetOffsetWidth && componentOffsetWidth) {
      setAdjustedTabWidthNum((tabSetOffsetWidth > (0.9 * componentOffsetWidth))
        ? (((0.9 * componentOffsetWidth) - 2) / tabs.length) : Number(tabWidth.replace('px', '')));
    }
  };

  useLayoutEffect(() => {
    recalculateTabWidth();
  }, []);

  useLayoutEffect(() => {
    setDisplayTabsDropdown(adjustedTabWidthNum < Number(minTabWidth.replace('px', '')));
  }, [adjustedTabWidthNum]);

  useLayoutEffect(() => {
    if (externalTabChanger) {
      setTabKey(tabs[externalTabChanger()]?.title);
    }
  }, [tabs, externalTabChanger]);

  const tabHandler = (event) => {
    const tabTitle = event.currentTarget.getAttribute('value');

    event.currentTarget.setAttribute('selected', true);
    setTabKey(tabTitle);

    if (onChange) {
      onChange(tabKeyMap[tabTitle].position);
    }
  };

  const dropdownHandler = (name, selection) => {
    const tabTitle = selection.value;

    setTabKey(tabTitle);

    if (onChange) {
      onChange(tabKeyMap[tabTitle].position);
    }
  };

  if (!listenerExists) {
    window.addEventListener('resize', recalculateTabWidth);
    setListenerExists(true);
  }

  let tabDisplay;

  if (displayTabsDropdown) {
    const options = Object.keys(tabKeyMap).map(key => ({label: key, value: key}));

    tabDisplay = <_Dropdown width={(adjustedTabWidthNum * tabs.length) + 'px'}>
      <div className='DropdownWrapper'>
        <Dropdown
          placeholder='Select An Option'
          defaultValue={{label: tabs[defaultSelection].title, value: tabs[defaultSelection].title}}
          dataPoint={{name: tabKey, value: {label: tabKey, value: tabKey}}}
          options={options}
          onChange={dropdownHandler}
          isClearable={false}
          isCreatable={false}
          isSearchable={true}
          disabled={false}
        />
      </div>
    </_Dropdown>;
  } else {
    const tabList = Object.keys(tabKeyMap).map((key, idx) => {
      let liContent;

      if (tabKeyMap[key].icon && !displayTabTitles) {
        liContent = <Icon svg={tabKeyMap[key].icon}/>;
      } else if (tabKeyMap[key].icon && displayTabTitles) {
        liContent = (
          <>
            <Icon src={tabKeyMap[key].icon} alt=''/>
            <span>{tabKeyMap[key].title}</span>
          </>
        );
      } else {
        liContent = <span>{tabKeyMap[key].title}</span>;
      }

      if (key === tabKey) {
        sliderOffset = (tabKeyMap[key].position * adjustedTabWidthNum);
      }

      return <_Tab
        key={idx}
        value={key}
        tabWidthNum={adjustedTabWidthNum}
        selected={key === tabKey}
        addMargin={displayTabTitles}
        onClick={tabHandler}
      >
        <_Title>{liContent}</_Title>
      </_Tab>;
    });

    const isLastTab = tabs.findIndex(tab => tab.title === tabKey) === tabs.length - 1;
    tabDisplay = <_TabSet ref={tabSetRef} width={(adjustedTabWidthNum * tabs.length) + 'px'}>
      <_Slider width={adjustedTabWidthNum - (isLastTab ? 2 : 0) + 'px'} offset={sliderOffset + (isLastTab ? 1 : 0)} tabsWidth={adjustedTabWidthNum * tabs.length + 'px'}>
        <div/>
        <span/>
      </_Slider>
      {tabList}
    </_TabSet>;
  }

  const contentList = tabs.map((tab, idx) => (
    <>{tab.title === tabKey ? <_Content key={idx}>
      {tab.content}
    </_Content> : null}</>
  ));

  return <_Wrapper layout={props.layout} ref={componentRef}>
    {tabDisplay}
    {contentList}
  </_Wrapper>;
};

Tabs.propTypes = {
  /**
   * the tabs array of tab objects with tab contents and title or optional icon
   */
  tabs: PropTypes.arrayOf(PropTypes.shape(
    {
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      content: PropTypes.object.isRequired,
    }
  )).isRequired,
  /**
   * boolean to display tab titles
   */
  displayTabTitles: PropTypes.bool.isRequired,
  /**
   * the minimum tab width
   */
  minTabWidth: PropTypes.string.isRequired,
  /**
   * the tab width
   */
  tabWidth: PropTypes.string.isRequired,
  /**
   * the default selected tab
   */
  defaultSelection: PropTypes.number.isRequired,
  /**
   * onChange function for parent component to use Tabs state if necessary onChange(tabIndex)
   */
  onChange: PropTypes.func,
  /**
   * optional function to change Tabs state externally if necessary
   */
  externalTabChanger: PropTypes.func,
  layout: layoutAttribute.props,
};

export default Tabs;
